import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useHintRegionComposition } from '~/hooks/apis/connection-google/useHintRegionComposition';
import {
  useHintRegionSettings,
  useUpsertHintRegionSetting,
} from '~/hooks/apis/connection-google/useHintRegionSettings';

import { HintCoverageAccountType, HintRegionSetting } from '~/services/ConnectionGoogleService';

// Mirrors the backend rules (2-letter region; 0 < weight ≤ 100, max 3 decimals) so most
// mistakes are caught before the request; the server remains the authority.
function validateRegion(region: string): string | null {
  return /^[A-Za-z]{2}$/.test(region) ? null : 'Region must be a 2-letter code';
}

function validateWeight(raw: string): string | null {
  const weight = Number(raw);
  if (raw.trim() === '' || Number.isNaN(weight)) {
    return 'Weight is required';
  }
  if (weight <= 0) {
    return 'Weight must be greater than 0 — disable the region instead of using 0';
  }
  if (weight > 100) {
    return 'Weight must be at most 100';
  }
  if (Math.round(weight * 1000) !== weight * 1000) {
    return 'Weight can have at most 3 decimal places';
  }
  return null;
}

// Weight fields accept at most 3 decimals as you type (NUMERIC(6,3) storage) — deeper
// precision never enters the draft, so the browser's native step tooltip can't trigger.
const WEIGHT_INPUT_PATTERN = /^\d*(\.\d{0,3})?$/;

// Spinner arrows move by 0.1 (finer precision is typed manually). min matches the step
// grid — a misaligned min (e.g. 0.001) makes the browser reject every 0.1 increment.
const WEIGHT_INPUT_PROPS = { min: 0.1, max: 100, step: 0.1 };

interface RowDraft {
  weight: string;
  enabled: boolean;
}

interface Props {
  accountType: HintCoverageAccountType;
}

export default function RegionSettingsPanel({ accountType }: Props) {
  const { data: settings, isLoading, isError } = useHintRegionSettings(accountType);
  const composition = useHintRegionComposition(accountType);
  const { mutate: upsertSetting, isPending: isSaving } = useUpsertHintRegionSetting();

  // Unsaved edits per region, overlaying the fetched rows. Cleared on successful save —
  // the invalidated settings query then re-renders the row from the server truth.
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [newRegion, setNewRegion] = useState('');
  const [newWeight, setNewWeight] = useState('1');

  const draftFor = (setting: HintRegionSetting): RowDraft =>
    drafts[setting.region] ?? { weight: String(setting.weight), enabled: setting.enabled };

  const isDirty = (setting: HintRegionSetting) => {
    const draft = draftFor(setting);
    return draft.enabled !== setting.enabled || Number(draft.weight) !== setting.weight;
  };

  const updateDraft = (setting: HintRegionSetting, patch: Partial<RowDraft>) => {
    setDrafts((prev) => ({ ...prev, [setting.region]: { ...draftFor(setting), ...patch } }));
  };

  const saveRow = (setting: HintRegionSetting) => {
    const draft = draftFor(setting);
    upsertSetting(
      { accountType, region: setting.region, weight: Number(draft.weight), enabled: draft.enabled },
      {
        onSuccess: () =>
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[setting.region];
            return next;
          }),
      },
    );
  };

  const newRegionError = newRegion === '' ? null : validateRegion(newRegion);
  const existingRegionError =
    newRegion !== '' && settings?.some((s) => s.region === newRegion.toUpperCase())
      ? 'Region already configured — edit its row instead'
      : null;
  const newWeightError = validateWeight(newWeight);

  const addRegion = () => {
    upsertSetting(
      { accountType, region: newRegion.toUpperCase(), weight: Number(newWeight), enabled: true },
      {
        onSuccess: () => {
          setNewRegion('');
          setNewWeight('1');
        },
      },
    );
  };

  const totalWeightedContribution =
    composition.data?.regions.reduce((sum, region) => sum + region.weightedContribution, 0) ?? 0;

  return (
    <Box mt={2}>
      <PageSubheader title="Region Settings" />
      <Paper sx={{ p: 2, mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Weights blend each region's Google opportunity counts into the classification score. Saved changes take effect
          at the next weekly reclassification — or click <strong>Re-classify Properties</strong> to apply them now.
        </Alert>
        {isLoading && <CircularProgress size={24} />}
        {isError && <Alert severity="error">Failed to load region settings</Alert>}
        {settings && (
          <Table size="small" aria-label="region settings">
            <TableHead>
              <TableRow>
                <TableCell>Region</TableCell>
                <TableCell width={160}>Weight</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell>Updated by</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.map((setting) => {
                const draft = draftFor(setting);
                const weightError = validateWeight(draft.weight);
                return (
                  <TableRow key={setting.region}>
                    <TableCell>
                      <Typography fontWeight="bold">{setting.region}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={draft.weight}
                        error={!!weightError}
                        helperText={weightError}
                        onChange={(e) => {
                          if (WEIGHT_INPUT_PATTERN.test(e.target.value)) {
                            updateDraft(setting, { weight: e.target.value });
                          }
                        }}
                        slotProps={{
                          htmlInput: { ...WEIGHT_INPUT_PROPS, 'aria-label': `${setting.region} weight` },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={draft.enabled}
                        onChange={(e) => updateDraft(setting, { enabled: e.target.checked })}
                        slotProps={{ input: { 'aria-label': `${setting.region} enabled` } }}
                      />
                    </TableCell>
                    <TableCell>{new Date(setting.updatedAt).toLocaleString()}</TableCell>
                    <TableCell>{setting.updatedBy ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!isDirty(setting) || !!weightError || isSaving}
                        onClick={() => saveRow(setting)}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Stack direction="row" spacing={2} alignItems="flex-start" mt={3}>
          <TextField
            size="small"
            label="New region"
            value={newRegion}
            error={!!newRegionError || !!existingRegionError}
            helperText={newRegionError ?? existingRegionError ?? '2-letter code, e.g. GB'}
            onChange={(e) => setNewRegion(e.target.value.toUpperCase())}
            slotProps={{ htmlInput: { maxLength: 2 } }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="Weight"
            type="number"
            value={newWeight}
            error={!!newWeightError}
            helperText={newWeightError}
            onChange={(e) => {
              if (WEIGHT_INPUT_PATTERN.test(e.target.value)) {
                setNewWeight(e.target.value);
              }
            }}
            slotProps={{ htmlInput: WEIGHT_INPUT_PROPS }}
            sx={{ width: 160 }}
          />
          <Button
            variant="contained"
            disabled={newRegion === '' || !!newRegionError || !!existingRegionError || !!newWeightError || isSaving}
            onClick={addRegion}
          >
            Add region
          </Button>
        </Stack>
      </Paper>

      <PageSubheader title="Regional Composition" />
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Each enabled region's share of the classified pool's weighted opportunity — the inputs the ranking actually
          used (forced properties excluded). Recomputed with the saved weights immediately; property scores follow at
          the next reclassification.
        </Typography>
        {composition.isLoading && <CircularProgress size={24} />}
        {composition.isError && <Alert severity="warning">Regional composition is unavailable</Alert>}
        {composition.data && (
          <Table size="small" aria-label="regional composition">
            <TableHead>
              <TableRow>
                <TableCell>Region</TableCell>
                <TableCell align="right">Weight</TableCell>
                <TableCell align="right">Raw opportunities</TableCell>
                <TableCell align="right">Properties</TableCell>
                <TableCell align="right">Weighted contribution</TableCell>
                <TableCell width="30%">Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {composition.data.regions.map((region) => {
                const share =
                  totalWeightedContribution > 0 ? (region.weightedContribution / totalWeightedContribution) * 100 : 0;
                return (
                  <TableRow key={region.region}>
                    <TableCell>
                      <Typography fontWeight="bold">{region.region}</Typography>
                    </TableCell>
                    <TableCell align="right">{region.weight}</TableCell>
                    <TableCell align="right">{region.rawTotal.toLocaleString()}</TableCell>
                    <TableCell align="right">{region.propertyCount.toLocaleString()}</TableCell>
                    <TableCell align="right">{region.weightedContribution.toLocaleString()}</TableCell>
                    <TableCell>
                      <Tooltip title={`${share.toFixed(1)}% of weighted opportunity`}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={share}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="body2" width={48} textAlign="right">
                            {share.toFixed(1)}%
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
